/**
 * Menu "Điều khiển" trên danh sách cụm loa (theo mẫu): bấm nút mở bảng 10 lệnh dạng lưới
 * biểu tượng, chọn một lệnh thì hỏi xác nhận (nêu số thiết bị) rồi gửi DV009 qua
 * POST /api/v1/devices/commands và hiện kết quả ba nhóm thành công / đang chờ / lỗi.
 *
 * Ánh xạ mã lệnh DV009 (Công văn 276) đã chốt với khách:
 *   khởi động lại 3; nâng cấp 4 (URL bộ cài); bật/tắt công suất 1 (1/0);
 *   phát tiếp 2/3; tạm dừng 2/2; bản tin tiếp theo 2/1; dừng phát tất cả 2/0;
 *   tắt loa = âm lượng 0; bật loa = âm lượng trước khi tắt của từng thiết bị.
 * "Dừng tiếp sóng" và "Phát theo lịch" của mẫu không có trong DV009 nên không hiển thị.
 * Trang Bảng điện tử dùng lại menu này (`dsNut` + `guiLenh` gửi DV017) — cùng mã lệnh CV276.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { dienVao } from '../ngon-ngu/vi';
import { viDieuKhienNhanh as t } from '../ngon-ngu/vi-dieu-khien-nhanh';
import { LoiApi, thietBiApi, type DongThietBi, type KetQuaHangLoat } from '../api/thiet-bi-api';
import { BangKetQuaHangLoat } from '../thanh-phan/KetQuaHangLoat';
import { BieuTuong } from '../thanh-phan/BieuTuong';
import { hienThongBaoNhanh } from '../thanh-phan/ThongBaoNhanh';
import { NGUONG_CHUYEN_MH09 } from './MH08NganDieuKhien';

export type MaNutDieuKhien = keyof typeof t.lenh;

/** Âm lượng dùng khi "Bật loa" mà không tra được âm lượng trước đó của thiết bị. */
export const AM_LUONG_MAC_DINH = 50;

interface DinhNghiaNut {
  ma: MaNutDieuKhien;
  maLenh: string;
  thamSo: string;
  mau: 'xanh' | 'do';
  hinh: ReactNode;
  khoiPhucAmLuong?: boolean;
}

const LOA = <path d="M4 9h4l5-4v14l-5-4H4z" />;
const COT_PHAT = (
  <>
    <circle cx="12" cy="10" r="2" />
    <path d="M8.5 13.5a5 5 0 0 1 0-7M15.5 6.5a5 5 0 0 1 0 7M5.6 16.4a9 9 0 0 1 0-12.8M18.4 3.6a9 9 0 0 1 0 12.8M12 12v9" />
  </>
);

/** Thứ tự và màu đúng mẫu (hai hàng, xanh là bật/chạy, đỏ là tắt/dừng). */
export const NUT_DIEU_KHIEN: DinhNghiaNut[] = [
  {
    ma: 'khoiDongLai',
    maLenh: '3',
    thamSo: '0',
    mau: 'xanh',
    hinh: (
      <>
        <path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      </>
    ),
  },
  { ma: 'nangCap', maLenh: '4', thamSo: '', mau: 'xanh', hinh: <path d="M12 4v11M7 10l5 5 5-5M5 20h14" /> },
  {
    ma: 'batLoa',
    maLenh: '0',
    thamSo: String(AM_LUONG_MAC_DINH),
    mau: 'xanh',
    khoiPhucAmLuong: true,
    hinh: (
      <>
        {LOA}
        <path d="M16 9a4 4 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11" />
      </>
    ),
  },
  {
    ma: 'tatLoa',
    maLenh: '0',
    thamSo: '0',
    mau: 'do',
    hinh: (
      <>
        {LOA}
        <path d="M16 9.5l5 5M21 9.5l-5 5" />
      </>
    ),
  },
  { ma: 'batCongSuat', maLenh: '1', thamSo: '1', mau: 'xanh', hinh: COT_PHAT },
  { ma: 'tatCongSuat', maLenh: '1', thamSo: '0', mau: 'do', hinh: COT_PHAT },
  {
    ma: 'phatTiep',
    maLenh: '2',
    thamSo: '3',
    mau: 'xanh',
    hinh: <path d="M8 5v14l11-7z" fill="currentColor" />,
  },
  {
    ma: 'tamDung',
    maLenh: '2',
    thamSo: '2',
    mau: 'do',
    hinh: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M5.6 5.6l12.8 12.8" />
      </>
    ),
  },
  {
    ma: 'banTinTiepTheo',
    maLenh: '2',
    thamSo: '1',
    mau: 'xanh',
    hinh: <path d="M4 6l8 6-8 6zM12 6l8 6-8 6z" />,
  },
  {
    ma: 'dungPhatTatCa',
    maLenh: '2',
    thamSo: '0',
    mau: 'do',
    hinh: (
      <>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <rect x="9" y="9" width="6" height="6" fill="currentColor" />
      </>
    ),
  },
];

const URL_HOP_LE = /^https?:\/\/\S+$/;

export function MenuDieuKhienThietBi({
  thietBi,
  choPhep,
  guiLenh,
  onXong,
  dsNut,
  tieuDeMenu = t.tieuDeMenu,
}: {
  thietBi: Pick<DongThietBi, 'id' | 'phuongThucKetNoi'>[];
  choPhep: boolean;
  guiLenh?: typeof thietBiApi.guiLenh;
  onXong: () => void;
  /** Chỉ hiện các nút này (giữ thứ tự mẫu); không truyền = đủ 10 lệnh của cụm loa. */
  dsNut?: readonly MaNutDieuKhien[];
  tieuDeMenu?: string;
}): ReactNode {
  const dsHien = dsNut ? NUT_DIEU_KHIEN.filter((n) => dsNut.includes(n.ma)) : NUT_DIEU_KHIEN;
  const goi = guiLenh ?? thietBiApi.guiLenh;
  const [mo, datMo] = useState(false);
  const [chon, datChon] = useState<DinhNghiaNut | null>(null);
  const [treGiay, datTreGiay] = useState('0');
  const [url, datUrl] = useState('');
  const [dangGui, datDangGui] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);
  const [ketQua, datKetQua] = useState<KetQuaHangLoat | null>(null);
  const vung = useRef<HTMLDivElement | null>(null);

  const so = thietBi.length;
  const soGianTiep = thietBi.filter((d) => d.phuongThucKetNoi === 'gian_tiep').length;

  useEffect(() => {
    if (!mo) return undefined;
    // Bấm ra ngoài hoặc Esc thì đóng menu.
    const ngoai = (e: MouseEvent): void => {
      if (vung.current && !vung.current.contains(e.target as Node)) datMo(false);
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') datMo(false);
    };
    document.addEventListener('mousedown', ngoai);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', ngoai);
      document.removeEventListener('keydown', esc);
    };
  }, [mo]);

  const dong = (): void => {
    const coKetQua = ketQua !== null;
    datChon(null);
    datKetQua(null);
    datLoi(null);
    if (coKetQua) onXong();
  };

  const thamSo = (n: DinhNghiaNut): string =>
    n.ma === 'khoiDongLai' ? treGiay.trim() || '0' : n.ma === 'nangCap' ? url.trim() : n.thamSo;

  const loiNhap = (n: DinhNghiaNut): string | null => {
    if (n.ma === 'nangCap' && (!URL_HOP_LE.test(url.trim()) || url.trim().length > 50))
      return t.urlKhongHopLe;
    if (n.ma === 'khoiDongLai') {
      const g = Number(treGiay || '0');
      if (!Number.isInteger(g) || g < 0 || g > 3600) return t.treKhongHopLe;
    }
    return null;
  };

  const gui = async (n: DinhNghiaNut, ids: string[]): Promise<void> => {
    datDangGui(true);
    datLoi(null);
    try {
      const kq = await goi({
        maLenh: n.maLenh,
        thamSo: thamSo(n),
        deviceIds: ids,
        ...(n.khoiPhucAmLuong ? { khoiPhucAmLuong: true } : {}),
      });
      // Không lỗi thì đóng hộp luôn, không hiện bảng kết quả (yêu cầu của khách).
      if (kq.loi.length === 0) {
        datChon(null);
        datKetQua(null);
        hienThongBaoNhanh(dienVao(t.daGui, { lenh: t.lenh[n.ma] }));
        onXong();
        return;
      }
      datKetQua(kq);
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : String(e));
    } finally {
      datDangGui(false);
    }
  };

  const loiHienTai = chon ? loiNhap(chon) : null;

  return (
    <div className="dkn" ref={vung}>
      <button
        type="button"
        className="nut nut--chinh"
        data-hanh-dong="dieu-khien"
        aria-haspopup="menu"
        aria-expanded={mo}
        disabled={!choPhep || so === 0}
        title={so === 0 ? t.goiY : undefined}
        onClick={() => datMo(!mo)}
      >
        <BieuTuong ten="dieu-khien" co={16} />
        {t.nut}
      </button>

      {mo ? (
        <div className="dkn__menu" role="menu" aria-label={tieuDeMenu} data-vung="menu-dieu-khien">
          {dsHien.map((n) => (
            <button
              key={n.ma}
              type="button"
              role="menuitem"
              className="dkn__muc"
              data-lenh={n.ma}
              onClick={() => {
                datMo(false);
                datTreGiay('0');
                datUrl('');
                datKetQua(null);
                datLoi(null);
                datChon(n);
              }}
            >
              <svg
                className={`dkn__hinh dkn__hinh--${n.mau}`}
                viewBox="0 0 24 24"
                width="26"
                height="26"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {n.hinh}
              </svg>
              <span>{t.lenh[n.ma]}</span>
            </button>
          ))}
        </div>
      ) : null}

      {chon ? (
        <div className="man-che" role="presentation">
          <div className="hop-thoai dkn__hop" role="dialog" aria-modal="true" aria-label={t.lenh[chon.ma]}>
            <h2>{t.lenh[chon.ma]}</h2>
            {ketQua ? (
              <BangKetQuaHangLoat
                ketQua={ketQua}
                dangThuLai={dangGui}
                onThuLaiPhanLoi={(ids) => void gui(chon, ids)}
                onDong={dong}
              />
            ) : (
              <>
                <p>{dienVao(t.moTa[chon.ma], { so, mucDinh: AM_LUONG_MAC_DINH })}</p>
                {chon.ma === 'khoiDongLai' ? (
                  <p className="truong">
                    <label htmlFor="dkn-tre">{t.treGiay}</label>
                    <input
                      id="dkn-tre"
                      type="number"
                      min={0}
                      max={3600}
                      value={treGiay}
                      onChange={(e) => datTreGiay(e.target.value)}
                    />
                  </p>
                ) : null}
                {chon.ma === 'nangCap' ? (
                  <>
                    <p className="truong">
                      <label htmlFor="dkn-url">{t.urlBoCai}</label>
                      <input
                        id="dkn-url"
                        type="url"
                        maxLength={50}
                        value={url}
                        placeholder="https://"
                        onChange={(e) => datUrl(e.target.value)}
                      />
                    </p>
                    {so > NGUONG_CHUYEN_MH09 ? (
                      <p className="hop-thoai__ghi-chu">
                        {dienVao(t.nangCapNhieu, { nguong: NGUONG_CHUYEN_MH09 })}
                      </p>
                    ) : null}
                  </>
                ) : null}
                {soGianTiep > 0 ? (
                  <p className="hop-thoai__ghi-chu">{dienVao(t.ghiChuGianTiep, { so: soGianTiep })}</p>
                ) : null}
                {loi ? (
                  <p className="dai-thong-bao dai-thong-bao--loi" role="alert">
                    {loi}
                  </p>
                ) : null}
                <div className="hop-thoai__nut">
                  <button type="button" className="nut" onClick={dong}>
                    {t.huy}
                  </button>
                  <button
                    type="button"
                    className={`nut ${chon.mau === 'do' ? 'nut--nguy-hiem' : 'nut--chinh'}`}
                    data-hanh-dong="gui-lenh"
                    disabled={dangGui || loiHienTai !== null}
                    title={loiHienTai ?? undefined}
                    onClick={() =>
                      void gui(
                        chon,
                        thietBi.map((d) => d.id),
                      )
                    }
                  >
                    {dangGui ? t.dangGui : t.gui}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
